import { Handle, Position } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import { ArrowDownFromLine, ArrowUpFromLine, ImageOff } from 'lucide-react'
import { buildShapePath } from '@/lib/imagePaths'
import type { Classification } from '@/features/classifications/api'
// The planning canvas's own sizing rule. Shared rather than copied: the canvas draws this box and
// ELK reserves room for it, and a second copy of the arithmetic is how two views of the same
// graphic end up different sizes.
import { SHAPE_LABEL_MIN_W, shapeBox } from '@/features/visualization/_legacy/utils/constants'
import type { ObjectDraft } from '../lib/template-graph'

export type TemplateNodeMode = 'Card' | 'Graphic'

/** The graphic an object draws, with the artwork's own pixel size when it was recorded. */
export interface ObjectGraphic {
    filename: string
    width?: number | null
    height?: number | null
}

/** How wide an object draws in Graphic mode: the artwork's box, but never narrower than its caption. */
export function graphicNodeWidth(graphic: ObjectGraphic | null | undefined): number {
    return Math.max(shapeBox(graphic?.width, graphic?.height).w, SHAPE_LABEL_MIN_W)
}

/** The caption under a graphic: its line plus the gap above it. */
const GRAPHIC_CAPTION_H = 18

/** How tall an object draws in Graphic mode: the artwork's box and the caption beneath it. */
export function graphicNodeHeight(graphic: ObjectGraphic | null | undefined): number {
    return shapeBox(graphic?.width, graphic?.height).h + GRAPHIC_CAPTION_H
}

/** A card's height: the object line and the classification line. */
export const CARD_NODE_H = 52

/**
 * A template object on the canvas: a KIND of object, not a real one. The card says what it is (the
 * classification) and which way it attaches to a hierarchy — the things an object actually decides.
 * It used to say how many of it too; every template object is one object now (Andrew, 2026-09-22).
 *
 * What it does NOT say is what the objects will be called. It used to print a worked example of
 * the name pattern with a sample prefix (ACME-VIRTUALMAC), which read as an Object ID that had
 * somehow already been assigned (Andrew, 2026-09-18). The pattern is still the pattern, and the
 * worked example is still shown where it is set — double-click the object — but a canvas of objects
 * has no business showing anything that looks like an identifier.
 *
 * Graphic mode draws what this object will draw on the planning canvas once the template is
 * placed: the classification's graphic whose tags best match the object's description, or its
 * default when nothing matches confidently. The page asks the server, which chooses with the planning canvas's
 * own matcher. A classification with no graphic assigned draws a marked placeholder rather than a
 * gap, so the template still reads and the omission is visible.
 *
 * Handles follow the dependency canvas: top is the SOURCE, bottom is the TARGET, so a dependency
 * is drawn upward from the thing underneath to the thing that needs it.
 */
export function TemplateObjectNode({ id, data, selected }: {
    id: string
    data: {
        object: ObjectDraft
        classification: Classification | null
        mode?: TemplateNodeMode
        graphic?: ObjectGraphic | null
    }
    selected?: boolean
}) {
    const { t } = useTranslation()
    const { object, classification, mode = 'Card', graphic = null } = data

    if (mode === 'Graphic') {
        // The box is the ARTWORK, at its own proportions — so a flat rack-mount unit is a flat box,
        // not a square with blank bands above and below it. And the handles live inside that box,
        // which is positioned, so Top and Bottom mean the top and bottom of the picture. They used
        // to be siblings of the caption, anchored to the whole node: connections stopped in the
        // empty band above the artwork and left from underneath the label (Andrew, 2026-09-21).
        // This is the structure the planning canvas's graphic node already has.
        const box = shapeBox(graphic?.width, graphic?.height)
        return (
            <div
                className="flex flex-col items-center"
                style={{ width: graphicNodeWidth(graphic) }}
                title={classification?.Name || ''}
            >
                <div
                    data-object-graphic
                    className={`relative flex items-center justify-center rounded-sm ${selected ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                    style={{ width: box.w, height: box.h, background: graphic ? undefined : 'hsl(var(--muted))' }}
                >
                    {graphic ? (
                        <img
                            src={buildShapePath(graphic.filename)}
                            alt={classification?.Name || object.ObjectKey}
                            className="block h-full w-full object-contain"
                            draggable={false}
                        />
                    ) : (
                        <ImageOff
                            className="h-5 w-5 text-muted-foreground"
                            aria-label={t('templates.editor.noGraphic', 'No graphic assigned to this classification')}
                        />
                    )}
                    <Handle type="source" position={Position.Top} id={`${id}-top`} />
                    <Handle type="target" position={Position.Bottom} id={`${id}-bottom`} />
                </div>
                <div className="relative z-[1] mt-1 max-w-full truncate text-center text-[10px] font-semibold">
                    {object.ObjectKey}
                </div>
            </div>
        )
    }

    return (
        <div
            className={`w-[210px] rounded-md border bg-background pb-1.5 text-left shadow-sm transition-shadow ${selected ? 'ring-2 ring-primary' : ''}`}
            style={{ borderTopColor: classification?.BackgroundColor || undefined, borderTopWidth: 3 }}
        >
            <Handle type="source" position={Position.Top} id={`${id}-top`} />
            <div className="flex items-center gap-1.5 px-2 pt-1.5">
                <span className="truncate text-xs font-semibold">{object.ObjectKey}</span>
                <span className="ml-auto flex items-center gap-0.5">
                    {object.AttachUp && (
                        <ArrowUpFromLine
                            className="h-3 w-3 text-muted-foreground"
                            aria-label={t('templates.editor.attachUp', 'Connects to the hierarchy above')}
                        />
                    )}
                    {object.AttachDown && (
                        <ArrowDownFromLine
                            className="h-3 w-3 text-muted-foreground"
                            aria-label={t('templates.editor.attachDown', 'Connects to the hierarchy below')}
                        />
                    )}
                </span>
            </div>
            <div className="truncate px-2 text-[10px] text-muted-foreground" title={classification?.Name || ''}>
                {classification?.Name || t('templates.editor.classGone', 'Classification unavailable')}
            </div>
            <Handle type="target" position={Position.Bottom} id={`${id}-bottom`} />
        </div>
    )
}
